/**
 * プロジェクトクレジット API クライアント。
 *
 * BE 仕様: `/api/<project_id>/credits` 配下に GET / POST / PATCH / DELETE / reorder。
 * クレジット項目は種別（item_type）ごとに `section` / `subsection` / `entry` / `track` /
 * `image` / `space` のいずれかであり、各種別ごとに独自の payload 形を持つ。
 *
 * 取得 API が一時的に失敗（404 等）してもユーザーが新規追加を続けられるように、
 * 書き込み系 mutation は `onQueryStarted` でローカルキャッシュを直接更新する。
 */
import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

// ===== 型定義 =====

export type CreditItemType =
  | "section"
  | "subsection"
  | "entry"
  | "track"
  | "image"
  | "space";

export interface CreditSection {
  title: string;
  title_en: string;
  note: string;
  note_en: string;
}

export interface CreditSubsection {
  title: string;
  title_en: string;
}

export interface CreditLinkedUser {
  user_id: number;
  display_name: string;
  profile_url: string;
}

export interface CreditEntry {
  role: string;
  role_en: string;
  display_name: string;
  display_name_en: string;
  link_url: string;
  linked_user: CreditLinkedUser | null;
}

export interface CreditTrackLine {
  line_id: string;
  order: number;
  label: string;
  label_en: string;
  value: string;
  value_en: string;
}

export interface CreditTrack {
  title: string;
  title_en: string;
  lines: CreditTrackLine[];
}

export interface CreditImage {
  image_key: string;
  alt_text: string;
  url: string;
}

export interface CreditSpace {
  lines: number;
}

export interface CreditItem {
  item_id: string;
  item_type: CreditItemType;
  order: number;
  section: CreditSection | null;
  subsection: CreditSubsection | null;
  entry: CreditEntry | null;
  track: CreditTrack | null;
  image: CreditImage | null;
  space: CreditSpace | null;
}

export interface CreditsListResponse {
  status: string;
  data: {
    credits: CreditItem[];
    count: number;
  };
}

export interface CreditDetailResponse {
  status: string;
  data: {
    credit: CreditItem;
  };
}

// ===== payload 型（POST/PATCH） =====

export type SectionPayload = Partial<CreditSection>;
export type SubsectionPayload = Partial<CreditSubsection>;
export interface EntryPayload {
  role?: string;
  role_en?: string;
  display_name?: string;
  display_name_en?: string;
  link_url?: string;
  linked_user_id?: number | null;
}
export interface TrackPayloadLine {
  label?: string;
  label_en?: string;
  value?: string;
  value_en?: string;
}
export interface TrackPayload {
  title?: string;
  title_en?: string;
  lines?: TrackPayloadLine[];
}
export interface ImagePayload {
  image_key?: string;
  alt_text?: string;
}
export interface SpacePayload {
  lines?: number;
}

export type CreditPayload =
  | SectionPayload
  | SubsectionPayload
  | EntryPayload
  | TrackPayload
  | ImagePayload
  | SpacePayload;

// ===== リクエスト型 =====

export interface GetCreditsParams {
  projectId: string;
}

export interface CreateCreditRequest {
  projectId: string;
  item_type: CreditItemType;
  order?: number;
  payload: CreditPayload;
}

export interface UpdateCreditRequest {
  projectId: string;
  itemId: string;
  order?: number;
  payload?: CreditPayload;
}

export interface DeleteCreditRequest {
  projectId: string;
  itemId: string;
}

export interface ReorderCreditsRequest {
  projectId: string;
  ordered_ids: string[];
}

// ===== 内部ヘルパ =====

/**
 * RTK Query の `getState()` から現在の credits 一覧を取り出す。
 * RootState 型が用意されていないため `unknown` で受け、専用のアクセスのみに限定する。
 */
const selectCurrentCredits = (
  state: unknown,
  projectId: string,
): CreditItem[] => {
  const selector = creditsApi.endpoints.getCredits.select({ projectId });
  type SelectorState = Parameters<typeof selector>[0];
  const current = selector(state as SelectorState);
  return current?.data?.data?.credits ?? [];
};

// ===== API =====

export const creditsApi = createApi({
  reducerPath: "creditsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["Credits"],
  endpoints: (builder) => ({
    getCredits: builder.query<CreditsListResponse, GetCreditsParams>({
      query: ({ projectId }) => `/api/${projectId}/credits`,
      providesTags: (_result, _error, { projectId }) => [
        { type: "Credits", id: projectId },
      ],
    }),

    createCredit: builder.mutation<CreditDetailResponse, CreateCreditRequest>({
      query: ({ projectId, item_type, order, payload }) => ({
        url: `/api/${projectId}/credits`,
        method: "POST",
        body: { item_type, order, payload },
      }),
      // 取得 API が失敗（404 等）していても作成結果を表示できるよう、ローカルキャッシュに直接反映する。
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const created = data.data.credit;
          const currentList = selectCurrentCredits(getState(), projectId);
          const next = [
            ...currentList.filter((c) => c.item_id !== created.item_id),
            created,
          ].sort((a, b) => a.order - b.order);
          dispatch(
            creditsApi.util.upsertQueryData("getCredits", { projectId }, {
              status: "success",
              data: { credits: next, count: next.length },
            }),
          );
        } catch {
          /* noop: エラーは呼び出し元で表示 */
        }
      },
    }),

    updateCredit: builder.mutation<CreditDetailResponse, UpdateCreditRequest>({
      query: ({ projectId, itemId, order, payload }) => ({
        url: `/api/${projectId}/credits/${itemId}`,
        method: "PATCH",
        body: {
          ...(order !== undefined ? { order } : {}),
          ...(payload !== undefined ? { payload } : {}),
        },
      }),
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const updated = data.data.credit;
          const currentList = selectCurrentCredits(getState(), projectId);
          const next = [
            ...currentList.filter((c) => c.item_id !== updated.item_id),
            updated,
          ].sort((a, b) => a.order - b.order);
          dispatch(
            creditsApi.util.upsertQueryData("getCredits", { projectId }, {
              status: "success",
              data: { credits: next, count: next.length },
            }),
          );
        } catch {
          /* noop */
        }
      },
    }),

    deleteCredit: builder.mutation<{ status: string; message: string }, DeleteCreditRequest>({
      query: ({ projectId, itemId }) => ({
        url: `/api/${projectId}/credits/${itemId}`,
        method: "DELETE",
      }),
      async onQueryStarted({ projectId, itemId }, { dispatch, getState, queryFulfilled }) {
        const currentList = selectCurrentCredits(getState(), projectId);
        const next = currentList.filter((c) => c.item_id !== itemId);
        const patchAction = creditsApi.util.upsertQueryData(
          "getCredits",
          { projectId },
          {
            status: "success",
            data: { credits: next, count: next.length },
          },
        );
        const undoData = {
          status: "success",
          data: { credits: currentList, count: currentList.length },
        };
        dispatch(patchAction);
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            creditsApi.util.upsertQueryData("getCredits", { projectId }, undoData),
          );
        }
      },
    }),

    reorderCredits: builder.mutation<CreditsListResponse, ReorderCreditsRequest>({
      query: ({ projectId, ordered_ids }) => ({
        url: `/api/${projectId}/credits/reorder`,
        method: "POST",
        body: { ordered_ids },
      }),
      // 楽観更新: 成功時に最新リストを返すのでキャッシュを上書き
      async onQueryStarted({ projectId, ordered_ids }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          creditsApi.util.updateQueryData("getCredits", { projectId }, (draft) => {
            const byId = new Map(draft.data.credits.map((c) => [c.item_id, c]));
            const reordered = ordered_ids
              .map((id, index) => {
                const item = byId.get(id);
                if (!item) return null;
                return { ...item, order: index };
              })
              .filter((c): c is CreditItem => c !== null);
            draft.data.credits = reordered;
          }),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            creditsApi.util.updateQueryData("getCredits", { projectId }, (draft) => {
              draft.data = data.data;
            }),
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetCreditsQuery,
  useCreateCreditMutation,
  useUpdateCreditMutation,
  useDeleteCreditMutation,
  useReorderCreditsMutation,
} = creditsApi;
